import React, { VFC, useMemo } from 'react';

import { Icon } from '@rbilabs/components-library';

import { ICombo } from '@rbi-ctg/menu';
import { useProductPropsSelections } from 'components/product-detail/use-product-props-selections';
import {
  getComboSlotOptionFromItem,
  transformItemToProductListItemProps,
} from 'components/product-detail/utils';
import { ProductListItem } from 'components/product-list-item';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard';

import { ProductNoteText } from '../styled';

import { IconContainer } from './product-customization-header.styled';
import { IProductCustomizationHeaderProps } from './types';

export const ProductCustomizationHeader: VFC<IProductCustomizationHeaderProps> = ({
  comboSlot,
  isClickable,
  isCollapsed,
  selectedItem,
  toggleCollapse,
  modifierSelections,
}) => {
  const { selectedProduct } = useProductWizardContext();
  const { priceForComboSlotSelection } = useMenuContext();
  const { buildProductPropsSelections } = useProductPropsSelections();
  const { formatCalories } = useMenuCalories();

  const productProps = useMemo(() => {
    let price = 0;
    if (comboSlot) {
      const selectedOption = getComboSlotOptionFromItem(comboSlot, selectedItem);
      if (selectedOption?.isPremium) {
        price = priceForComboSlotSelection({
          combo: selectedProduct as ICombo,
          comboSlot,
          selectedOption,
        });
      }
    }

    const selections = buildProductPropsSelections(modifierSelections);

    const listItemProps = transformItemToProductListItemProps(selectedItem, {
      price,
      productNote: comboSlot?.name.locale,
      selections,
    });

    return listItemProps.productProps;
  }, [
    buildProductPropsSelections,
    comboSlot,
    modifierSelections,
    priceForComboSlotSelection,
    selectedItem,
    selectedProduct,
  ]);

  const controls = isClickable && (
    <IconContainer collapsed={isCollapsed}>
      <Icon title="toggle-selections" icon="arrowUp" color="icon-default" width="14px" />
    </IconContainer>
  );

  return (
    <div data-testid="customization-header-selected">
      <ProductListItem
        isClickable={isClickable}
        onProductClick={toggleCollapse}
        productProps={{
          ...productProps,
          calories: formatCalories(productProps.calories),
          note: <ProductNoteText>{productProps.note}</ProductNoteText>,
        }}
        controls={controls}
      />
    </div>
  );
};
